import { ChevronDown, ChevronUp, Undo2, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BULK_ACTION_LABELS, bulkResultMessage, type PipelineActionView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { formatNumber } from '../../../lib/format';
import { useNow } from '../../../lib/use-now';
import { isRunning, usePipelineAction } from '../api/pipeline-api';
import styles from './ActionReport.module.css';

const SECOND_MS = 1000;
const PERCENT = 100;

interface ActionReportProps {
  readonly actionId: string;
  readonly onUndo: (actionId: string) => void;
  readonly onFinished: (view: PipelineActionView) => void;
  readonly onClose: () => void;
}

/**
 * §L9's progress indicator and result report, in one strip above the pipeline: a
 * running count while a background action works through its records, then "27 of 30
 * updated. 3 failed — see details" with each failure and its reason, and Undo for as
 * long as the ten minutes last. Local to the list — the rest of the page never waits.
 */
export function ActionReport({ actionId, onUndo, onFinished, onClose }: ActionReportProps): ReactNode {
  const action = usePipelineAction(actionId);
  const view = action.data;
  // Open by default once something has failed; after that, the recruiter's choice.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const wasRunning = useRef(false);
  const now = useNow(SECOND_MS);

  // The moment a background action finishes, the recruiter is told — once.
  useEffect(() => {
    if (!view) return;
    if (isRunning(view)) {
      wasRunning.current = true;
      return;
    }
    if (!wasRunning.current) return;
    wasRunning.current = false;
    onFinished(view);
  }, [view, onFinished]);

  if (!view) return null;
  const running = isRunning(view);
  const label = BULK_ACTION_LABELS[view.kind];
  const percent = view.requestedCount === 0 ? 0 : Math.round((view.processedCount / view.requestedCount) * PERCENT);
  const secondsLeft = view.undoableUntil ? Math.max(0, Math.floor((new Date(view.undoableUntil).getTime() - now) / SECOND_MS)) : 0;
  const problems = view.undoneAt ? view.undoRefusals : view.failures;
  const isOpen = toggled ?? (!running && problems.length > 0);

  return (
    <section className={styles.report} aria-live="polite" aria-label={`${label} on ${view.requestedCount}`}>
      <div className={styles.line}>
        {running ? (
          <>
            <span className={styles.message}>
              {label}: {formatNumber(view.processedCount)} of {formatNumber(view.requestedCount)}
              <span className={styles.meta}> — carry on working; you will be told when it is done</span>
            </span>
            <span className={styles.track} role="progressbar" aria-valuemin={0} aria-valuemax={PERCENT} aria-valuenow={percent} aria-label={`${label} progress`}>
              <span className={styles.fill} style={{ inlineSize: `${percent}%` }} />
            </span>
          </>
        ) : (
          <span className={styles.message}>
            {view.undoneAt ? `Undone — ${view.undoneCount} put back${view.undoRefusals.length > 0 ? `, ${view.undoRefusals.length} could not be` : ''}.` : bulkResultMessage(view)}
          </span>
        )}
        <span className={styles.actions}>
          {problems.length > 0 && (
            <Button variant="quiet" size="sm" icon={isOpen ? ChevronUp : ChevronDown} onClick={() => setToggled(!isOpen)} aria-expanded={isOpen}>
              {isOpen ? 'Hide details' : 'Details'}
            </Button>
          )}
          {view.canUndo && secondsLeft > 0 && (
            <Button variant="outline" size="sm" icon={Undo2} onClick={() => onUndo(view.id)}>
              Undo · {clock(secondsLeft)}
            </Button>
          )}
          {!running && <Button variant="ghost" size="sm" icon={X} aria-label="Close the report" onClick={onClose} />}
        </span>
      </div>
      {isOpen && problems.length > 0 && (
        <ul className={styles.failures}>
          {problems.map((failure) => (
            <li key={failure.applicationId} className={styles.failure}>
              <span className={styles.name}>{failure.candidateName}</span>
              <span className={styles.reason}>{failure.message}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function clock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}
